import {
	InputField,
	Select,
	TableFilterWrapper,
} from '@welbe/web-design-system';
import { OptionProps } from '@welbe/web-design-system/lib/components/molecules/Select/Select';
import useBranchOfficeSelect from 'Factories/filters/hooks/useBranchOfficeSelect';
import { useEffect, useMemo, useState } from 'react';
import { loggerInfo } from 'utils/infoLog';
import { handleKeyPress } from 'utils/keyPressHandler';
import { getBusinessNames } from 'Views/Candidates/components/CandidateForm/services';
import { getHrUsers } from '../../services';
import { optionsMedicStatus, optionsStatusRH } from '../../utils';

type CandidatesFiltersProps = {
	height: number;
	showBusinessNames?: boolean;
	onChangeFilters: (filter: { [key: string]: any }) => void;
	onSearch: () => void;
};

export const CandidatesFilters = ({
	height,
	showBusinessNames,
	onChangeFilters,
	onSearch,
}: CandidatesFiltersProps) => {
	const [businessNamesOptions, setBusinessNamesOptions] = useState<any[]>([]);
	const [hrUsers, setHrUsers] = useState<OptionProps[]>([]);
	const {
		branchOfficeOptions,
		selectedBranchOffice,
		isLoadingBranchOffice,
		handleSelectBranchOffice,
	} = useBranchOfficeSelect();

	const handleChangeMultipleChoices = (data: any, key: string) => {
		const transformedData = data.map((item: any) => item.id).join(',');
		onChangeFilters({ [key]: transformedData });
	};

	const fetchHrUsers = async () => {
		try {
			const { data } = await getHrUsers();
			setHrUsers([
				{ id: '', value: '' },
				...data.content.map((user: any) => ({ id: user.id, value: user.name })),
			]);
		} catch (error) {
			loggerInfo({
				data: error,
				context: 'candidate',
				location: 'hrUsers',
			});
		}
	};

	const fetchBusinessNames = async () => {
		try {
			const {
				data: { content },
			} = await getBusinessNames();

			setBusinessNamesOptions([
				{
					id: '',
					value: 'Selecciona una razón social',
				},
				...content.map((businessName: any) => ({
					id: businessName.id,
					value: businessName.name,
				})),
			]);
		} catch (error) {
			loggerInfo({
				data: error,
				context: 'candidate',
				location: 'businessName',
			});
		}
	};

	useMemo(() => {
		if (selectedBranchOffice) {
			onChangeFilters({
				destinationBranchOfficeId: selectedBranchOffice.id || '',
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedBranchOffice]);

	useEffect(() => {
		fetchHrUsers();
	}, []);

	useEffect(() => {
		if (showBusinessNames) {
			fetchBusinessNames();
		}
		fetchHrUsers();
	}, [showBusinessNames]);

	return (
		<TableFilterWrapper height={height}>
			<InputField
				id="candidateName"
				background="white"
				placeholder="Busca por nombre de candidato"
				label="Candidato"
				onKeyPress={(e) => handleKeyPress(e, onSearch)}
				onChange={(data) => onChangeFilters({ name: data })}
			/>
			<Select
				id="candidateDoctorStatus"
				multiple
				inputBackground="white"
				placeholder="Selecciona un estatus"
				label="Estatus médico"
				options={optionsMedicStatus}
				onChange={(data) =>
					handleChangeMultipleChoices(data, 'doctorApprovalStatus')
				}
				topDistanceCheckBox="4px"
			/>
			<Select
				id="candidateRhStatus"
				multiple
				inputBackground="white"
				placeholder="Selecciona un estatus"
				label="Estatus RH"
				options={optionsStatusRH}
				onChange={(data) =>
					handleChangeMultipleChoices(data, 'hrApprovalStatus')
				}
				topDistanceCheckBox="4px"
			/>
			<Select
				id="candidateBranchOffice"
				disabled={isLoadingBranchOffice}
				inputBackground="white"
				placeholder="Selecione..."
				loading={isLoadingBranchOffice}
				label="Sucursal"
				required
				initialSelected={selectedBranchOffice}
				options={branchOfficeOptions}
				onChange={handleSelectBranchOffice}
			/>
			<InputField
				id="candidateSector"
				background="white"
				placeholder="Busca por sector"
				label="Puesto"
				onKeyPress={(e) => handleKeyPress(e, onSearch)}
				onChange={(data) => onChangeFilters({ position: data || '' })}
			/>
			{showBusinessNames && (
				<Select
					label="Razón social"
					searchable
					inputBackground="white"
					options={businessNamesOptions}
					placeholder="Selecciona una razón social"
					onChange={(select) =>
						onChangeFilters({ customBusinessNameId: select.id || '' })
					}
				/>
			)}
			<Select
				label="Responsable RH"
				searchable
				inputBackground="white"
				options={hrUsers}
				onChange={(select) => onChangeFilters({ companyUserId: select.id })}
				placeholder="Selecciona un reponsable"
			/>
		</TableFilterWrapper>
	);
};
